import { ref, computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { isEqual, isEmpty } from 'lodash-es';
import { getAsyncSearchResults } from '@/api/asyncSearch';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useModals } from '@/stores/modals/modals';
import { useAppPagination } from '@/stores/app/pagination';
import { useAppLoading } from '@/stores/app/loading';
import { useAppRoute } from '@/stores/app/route';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { emitter } from '@/helpers/emitter';
import { format } from 'date-fns';
import {
  fetchProcessInsightsFilters,
  fetchSourceInsightsFilters,
  fetchReqLibraryFilters,
  fetchJobProfileLibraryFilters,
  fetchEmployeeAcknowledgementsLibraryFilters,
  fetchFlsaSurveysLibraryFilters,
  fetchApprovalsLibraryFilters,
} from '@/api/filters';
import { removeEmptyParams, previewQueryParams } from '@/stores/filters/helpers';
import { getQuarter } from '@/helpers/date';
import { getDemographicSurveyFilters } from '@/api/demographicSurveyReport';
import { modeMap } from '@/stores/requisitions/helpers';
import { featureQueryParams } from '@/api/helpers/insights/insights';

export const useFilters = defineStore('filters', () => {
  // Other Stores
  const { setDateRange } = useDatePicker();
  const { tempDateRange, dateWeekIndex, dateRange } = storeToRefs(useDatePicker());
  const {
    setSubMenuOptions,
    setSelectedSubMenuOption,
    setSubMenuDisabled,
  } = useSubMenu();
  const { selectedSubMenuOption } = storeToRefs(useSubMenu());
  const { setModal } = useModals();
  const { show } = storeToRefs(useModals());
  const { setPageNumber } = useAppPagination();
  const { addErrorSnackbar, addSnackbar } = useAppSnackbar();

  const { route } = storeToRefs(useAppRoute());

  // State
  const fields = ref([]);
  const tempFilters = ref([]);
  const myJobsEnabled = ref(false);
  const enforcedMyJobsEnabled = ref(false);
  const searchTerm = ref('');

  const filterConfig = ref({
    module: '',
    endpoint: '',
    showMyJobs: false,
    datepicker: false,
    subMenuType: null,
    ignorableFilters: [],
  });

  // Getters
  const getAlwaysOn = computed(() => {
    const filteredFields = fields.value.filter((field) => field.alwaysOn);
    return filteredFields.length;
  });

  const activeFilters = computed(() => fields.value.some((field) => field.value.length));

  const noActiveFilters = computed(() => Boolean(
    getAlwaysOn.value || activeFilters.value,
  ));

  const mergedTempFilters = computed(() => {
    const filters = {};
    tempFilters.value.forEach((filter) => {
      if (filter.value.length) {
        filters[filter.id] = filter.value.map((value) => value.id);
      }
    });
    return filters;
  });

  const selectedFiltersLabels = computed(() => fields.value
    .filter((field) => field.value.length)
    .map((field) => ({
      label: field.label,
      values: field.value.map((value) => value.label).join(', '),
    })));

  const trackingFilters = computed(() => {
    /* compare last successful filter request (fields) to newly-selected filters (tempFilters) */
    const filterTypes = [];
    fields.value.forEach((field, index) => {
      if (!isEqual(field.value, tempFilters.value[index].value)) {
        filterTypes.push(field.label);
      }
    });
    return filterTypes;
  });

  const areFiltersApplied = computed(() => fields.value.some((field) => field.value.length));

  // True if ANY applied filter is not in ignorableFilters
  const areNonIgnorableFiltersApplied = computed(() => {
    const ignored = filterConfig.value.ignorableFilters || [];

    return fields.value.some(
      (field) => field.value.length && !ignored.includes(field.id),
    );
  });

  // Actions
  const setFilterConfig = (props) => {
    Object.entries(props).forEach(([key, value]) => {
      filterConfig.value[key] = value;
    });
  };

  const setFilterFields = (newFields) => {
    const filteredFields = newFields.filter((filter) => filter !== null);
    const convertLabelsToString = (items) => items.map((item) => {
      if (typeof item.label !== 'string') {
        if (typeof item.label === 'boolean') {
          return {
            ...item,
            label: item.label ? 'Yes' : 'No',
          };
        }
        return {
          ...item,
          label: String(item.label),
        };
      }
      return item;
    });

    tempFilters.value = filteredFields.map((field, index) => ({
      ...field,
      options: convertLabelsToString(field.options),
      value: convertLabelsToString(filteredFields[index].value),
      loadOptions: field.async ? function loadOptionsCallback({
        action, searchQuery, callback,
      }) {
        if (
          field.async
          && searchQuery.length >= 2
          && action === 'ASYNC_SEARCH'
        ) {
          getAsyncSearchResults({
            query: searchQuery,
            urlObj: field.asyncUrl,
          })
            .then((res) => {
              /* callback notifies vue-treeselect about data population completion */
              callback(null, res);
            });
        }
      } : null,
    }));
    fields.value = filteredFields.map((field, index) => ({
      ...field,
      options: convertLabelsToString(field.options),
      value: convertLabelsToString(filteredFields[index].value),
    }));
  };

  const setTempFilters = ({ value, index }) => {
    const newTempFilters = [...tempFilters.value];
    newTempFilters[index].value = value;
    tempFilters.value = newTempFilters;
  };

  const setMyJobsEnabled = ({ value = false, enforced = false }) => {
    myJobsEnabled.value = value;
    enforcedMyJobsEnabled.value = enforced;
  };

  const setSearchTerm = (value) => {
    searchTerm.value = value || '';
  };

  const resetValues = ({ fieldIndex, setToAppliedFilters = false } = {}) => {
    // clear individual field value if fieldIndex is passed, else clear values for all fields
    if (typeof fieldIndex !== 'undefined') {
      tempFilters.value[fieldIndex].value = setToAppliedFilters
        ? fields.value[fieldIndex].value : [];
    } else {
      // Do not clear ignorable filters
      const ignored = filterConfig.value.ignorableFilters || [];

      fields.value.forEach((field, index) => {
        if (ignored.includes(field.id)) return;

        tempFilters.value[index].value = setToAppliedFilters ? fields.value[index].value : [];
      });
    }
  };

  const resetMultipleValues = ({ indices, setToAppliedFilters = false }) => {
    if (!indices) return;

    indices.forEach((index) => {
      resetValues({ fieldIndex: index, setToAppliedFilters });
    });
  };

  const clearPersistentFilters = () => {
    searchTerm.value = '';
    myJobsEnabled.value = false;
  };

  const updateFilters = ({
    filters,
    subMenuOptions,
    dateRange: newDateRange,
    myJobsEnabled: newMyJobsEnabled,
    searchTerm: newSearchTerm,
  }) => {
    if (filters) { setFilterFields(filters); }
    if (newDateRange) { setDateRange(newDateRange); }
    if (newMyJobsEnabled) { setMyJobsEnabled(newMyJobsEnabled); }
    if (newSearchTerm) { setSearchTerm(newSearchTerm.value); }
    if (subMenuOptions) {
      setSubMenuOptions(subMenuOptions);
      setSelectedSubMenuOption(subMenuOptions.value);
      setSubMenuDisabled(subMenuOptions.disabled || false);
    } else setSubMenuOptions({ options: [] });
  };

  const sendFilterRequest = ({
    pageNumber, router, resetData,
  }) => {
    if (show.value.filter) {
      setModal({ field: 'filter', value: false });
    }

    let page = '';
    if (![
      'inbound-report',
      'outbound-report',
      'overall-report',
      'drop-off',
      'pass-through',
      'gender-report',
      'platform-permissions',
    ].includes(route.value.name)) {
      /* If pagination is clicked, send specific page number,
      otherwise set page to 1 before sending */
      page = pageNumber || 1;
      setPageNumber(page);
    }

    let dateRangeToUse = {};

    if (!isEmpty(tempDateRange.value)) {
      dateRangeToUse = {
        from: format(tempDateRange.value[0], 'yyyy-MM-dd'),
        to: format(tempDateRange.value[1], 'yyyy-MM-dd'),
      };
    } else if (dateRange.value?.from && dateRange.value?.to) {
      dateRangeToUse = {
        from: dateRange.value.from,
        to: dateRange.value.to,
      };
    }

    /* send current route name and filters to reload current page with filter data */
    emitter.emit('updatedFilters', {
      router,
      page,
      mode: route.value.name,
      selectedFilters: mergedTempFilters.value,
      selectedMyJobs: myJobsEnabled.value,
      searchTerm: searchTerm.value,
      selectedDateRange: dateRangeToUse,
      selectedDateWeek: dateWeekIndex.value || router.currentRoute?.value?.query?.week,
      resetData,
      selectedSubMenuOption: selectedSubMenuOption.value,
    });
  };

  const submitSubMenu = (({ optionId, router }) => {
    setSelectedSubMenuOption(optionId);
    sendFilterRequest({ router });
  });

  const submitMyJobs = ({ myJobsEnabled: newMyJobsEnabled, router }) => {
    setMyJobsEnabled({ value: newMyJobsEnabled, enforced: enforcedMyJobsEnabled.value });
    sendFilterRequest({ router });
  };

  const submitSearchTerm = ({
    searchTerm: newSearchTerm, router, defaultSort = 'relevance', clearSort,
  }) => {
    if (newSearchTerm) {
      setSelectedSubMenuOption(defaultSort);
    } else {
      const reqDateSort = route.value.name === 'open-requisitions' ? '-opened_at' : '-closed_at';
      const isRequisitionLibrary = route.value.name.includes('requisitions');

      setSelectedSubMenuOption(clearSort || (isRequisitionLibrary ? reqDateSort : '-date'));
    }
    setSearchTerm(newSearchTerm);
    sendFilterRequest({ router });
  };

  const setUserFilter = async ({ user, router }) => {
    const newFields = fields.value.map((field) => {
      if (field.id === 'user') {
        const alreadyHasUserSelected = field.value.some(({ id }) => id === user.userId);

        return {
          ...field,
          value: alreadyHasUserSelected ? field.value : [...field.value, user],
        };
      }

      return field;
    });

    updateFilters({ filters: newFields });
    sendFilterRequest({ router });
  };

  const clearValues = ({ index, router }) => {
    resetValues({ fieldIndex: index });
    sendFilterRequest({ router });
  };

  const clearMultipleValues = ({ indices, router }) => {
    if (!indices) return;

    indices.forEach((index) => {
      resetValues({ fieldIndex: index });
    });
    sendFilterRequest({ router });
  };

  const loadFilters = async ({
    selectedDateRange,
    routeQuery,
    router,
    selectedFilters,
    selectedMyJobs,
    mode = '',
    searchTerm: newSearchTerm,
    page = null,
    sort = null,
  }) => {
    const lastQuarter = getQuarter(1);
    const fallbackDateRange = selectedDateRange || { from: format(lastQuarter[0], 'yyyy-MM-dd'), to: format(lastQuarter[1], 'yyyy-MM-dd') };
    const date = routeQuery?.from && routeQuery?.to
      ? { from: routeQuery.from, to: routeQuery.to }
      : fallbackDateRange;

    useAppLoading().setLoading({
      filtersLoading: true,
    });

    const fetchFunction = {
      process: fetchProcessInsightsFilters,
      source: fetchSourceInsightsFilters,
      demographicSurvey: getDemographicSurveyFilters,
      requisitionLibrary: fetchReqLibraryFilters,
      jobProfileLibrary: fetchJobProfileLibraryFilters,
      employeeAcknowledgementsLibrary: fetchEmployeeAcknowledgementsLibraryFilters,
      flsaSurveysLibrary: fetchFlsaSurveysLibraryFilters,
      approvalsLibrary: fetchApprovalsLibraryFilters,
    }[filterConfig?.value?.endpoint] || fetchProcessInsightsFilters;

    const pageSpecficParams = {
      requisitionLibrary: {
        mode: modeMap[mode] || mode,
        searchTerm: newSearchTerm,
      },
      jobProfileLibrary: {
        searchTerm: newSearchTerm,
      },
      employeeAcknowledgementsLibrary: {
        searchTerm: newSearchTerm,
      },
      flsaSurveysLibrary: {
        searchTerm: newSearchTerm,
      },
      approvalsLibrary: {
        searchTerm: newSearchTerm,
      },
    }[filterConfig?.value?.endpoint] || {};

    const additionalParams = {};

    if (filterConfig?.value?.showMyJobs) {
      additionalParams.owned = selectedMyJobs;
    }

    if (filterConfig?.value?.datepicker) {
      additionalParams.selectedDateRange = date;
    }

    try {
      const {
        filters = [],
        owned = {},
        dateRange: newDateRange,
      } = await fetchFunction({
        ...pageSpecficParams,
        ...additionalParams,
        routeQuery,
        filters: selectedFilters,
      });

      updateFilters({
        dateRange: newDateRange,
        filters,
        myJobsEnabled: owned,
      });

      if ( // If newDateRange does not exist, we're probably on a req lib page, don't show snackbar
        (newDateRange && date?.from !== newDateRange.from)
        || (newDateRange && date?.to !== newDateRange.to)
      ) {
        addSnackbar({
          color: 'yellow500',
          icon: '$information-outline',
          text: 'Date range defaulting to custom: Data not available for the entirety of the timeframe selected.',
        });
      }

      const prunedRootStateQuery = removeEmptyParams(route.value?.query);
      const prunedRouteQuery = removeEmptyParams(routeQuery) || {};
      const dateAvailable = !prunedRootStateQuery?.to
        && !prunedRootStateQuery?.from
        && !prunedRouteQuery?.to
        && !prunedRouteQuery?.from
        && newDateRange?.from
        && newDateRange?.to;

      // Ensure we keep feature query params
      featureQueryParams.forEach((param) => {
        if (route.value?.query?.[param]) {
          prunedRouteQuery[param] = route.value.query[param];
        }
      });

      const usePage = {
        requisitionLibrary: true,
        jobProfileLibrary: true,
        flsaSurveysLibrary: true,
        employeeAcknowledgementsLibrary: true,
        approvalsLibrary: true,
      }[filterConfig?.value?.endpoint] || false;

      /* Checks if the query changed,
        if there are selected date range and no query,
        if the merged temp filters differ from the route query,
        and if we need to add the search query, owned, page and sort
      */
      if (!router) return;

      let newQuery = {};
      if (!isEqual(prunedRootStateQuery, prunedRouteQuery)) {
        newQuery = { ...prunedRouteQuery };
      }
      if (
        (filterConfig?.value?.datepicker
          && (!isEqual(prunedRouteQuery, { from: newDateRange?.from, to: newDateRange?.to })
          || dateAvailable)
        )
      ) {
        newQuery.from = newDateRange?.from;
        newQuery.to = newDateRange?.to;
      }
      if (!isEqual(mergedTempFilters.value, prunedRouteQuery)) {
        newQuery = { ...newQuery, ...mergedTempFilters.value };
      }
      if (newSearchTerm) {
        newQuery.q = newSearchTerm;
      }
      if (owned?.value && filterConfig?.value?.showMyJobs) {
        newQuery.owned = owned.value;
      }
      if (usePage) {
        newQuery.page = page;
      }
      if (sort) {
        newQuery.sort = sort;
      }

      // Ensure we keep sidebar preview query params
      previewQueryParams.forEach((param) => {
        if (route.value?.query?.[param]) {
          newQuery[param] = route.value.query[param];
        }
      });

      router.push({
        query: newQuery,
      }).catch(() => true); // ignore duplicate navigation error due to global filter selection
    } catch (error) {
      addErrorSnackbar({
        text: `Unable to load filters - ${error.message}`,
      });
      throw error;
    } finally {
      useAppLoading().setLoading({
        filtersLoading: false,
      });
    }
  };

  return {
    // State
    fields,
    tempFilters,
    myJobsEnabled,
    enforcedMyJobsEnabled,
    searchTerm,

    filterConfig,

    // Getters
    getAlwaysOn,
    activeFilters,
    noActiveFilters,
    mergedTempFilters,
    trackingFilters,
    areFiltersApplied,
    areNonIgnorableFiltersApplied,
    selectedFiltersLabels,

    // Actions
    setFilterConfig,
    setFilterFields,
    setTempFilters,
    setMyJobsEnabled,
    setSearchTerm,
    resetValues,
    resetMultipleValues,
    clearPersistentFilters,

    updateFilters,
    sendFilterRequest,
    submitSubMenu,
    submitMyJobs,
    submitSearchTerm,
    setUserFilter,
    clearValues,
    clearMultipleValues,
    loadFilters,
  };
});
